/**
 * AssignExistingMemberDialog — put someone already in the directory onto this
 * unit's roll. No application: that flow is for people joining from outside.
 *
 * Works at every level, so a Province can hold its own members without them
 * having to apply to an assembly as well.
 */
import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { useChurchRoles } from '@/lib/churchRoles';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, Loader2, UserCheck, ArrowRightLeft, Users } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const call = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Request failed');
  return body;
};

export const AssignExistingMemberDialog = ({ churchId, onClose, onSaved }) => {
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [picked, setPicked] = useState(null);
  const [role, setRole] = useState('member');
  const [mode, setMode] = useState('also');
  const [status, setStatus] = useState('active');
  const { groups: roleGroups } = useChurchRoles();

  const { data, isFetching } = useQuery({
    queryKey: ['directory-search', churchId, term],
    queryFn: () => call(`/api/church/${churchId}/directory-search?q=${encodeURIComponent(term)}`),
    enabled: term.length >= 2,
    retry: false,
  });
  const candidates = data?.candidates || [];

  const save = useMutation({
    mutationFn: () => call(`/api/church/${churchId}/members/assign`, {
      method: 'POST',
      body: JSON.stringify({
        person_id: picked?.person_id || '',
        source_member_id: picked?.source_member_id || '',
        first_name: (picked?.name || '').split(' ')[0],
        last_name: (picked?.name || '').split(' ').slice(1).join(' '),
        email: picked?.email || '',
        phone: picked?.phone || '',
        role, mode, status,
      }),
    }),
    onSuccess: (res) => {
      toast.success(res.transferred_from?.length
        ? `${res.name} transferred here from ${res.transferred_from.map((t) => t.church_name).join(', ')}`
        : `${res.name} added to this roll`);
      onSaved();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="assign-existing-dialog">
        <DialogHeader>
          <DialogTitle>Add an existing member</DialogTitle>
          <DialogDescription>
            Search anyone already known to the movement — a member of another unit, a delegate,
            a student. No application needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <form onSubmit={(e) => { e.preventDefault(); setTerm(q.trim()); setPicked(null); }}
                className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8"
                     placeholder="Name, email or phone number" data-testid="assign-search-input" />
            </div>
            <Button type="submit" variant="outline" data-testid="assign-search-btn">Search</Button>
          </form>

          {isFetching && (
            <p className="text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Searching the directory…</p>
          )}

          {term.length >= 2 && !isFetching && candidates.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="assign-no-results">
              Nobody in the directory matches that. Use <b>Add member</b> to create a new record.
            </p>
          )}

          {candidates.length > 0 && (
            <div className="max-h-64 overflow-y-auto divide-y rounded-md border border-slate-200"
                 data-testid="assign-results">
              {candidates.map((c) => (
                <button type="button" key={`${c.person_id}-${c.email}-${c.phone}`}
                        disabled={c.already_here}
                        onClick={() => setPicked(c)}
                        className={`w-full text-left p-2.5 hover:bg-slate-50 disabled:opacity-50 ${picked === c ? 'bg-emerald-50' : ''}`}
                        data-testid={`assign-candidate-${c.person_id || c.source_member_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-800">{c.name || 'Unnamed'}</span>
                    {c.already_here
                      ? <Badge variant="outline" className="text-[9px]">already here</Badge>
                      : picked === c && <UserCheck className="h-4 w-4 text-emerald-600" />}
                  </div>
                  <p className="text-[11px] text-slate-500">
                    {[c.email, c.phone].filter(Boolean).join(' · ') || 'no contact details'}
                  </p>
                  {c.sources?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1"
                         data-testid={`assign-candidate-sources-${c.person_id || c.source_member_id}`}>
                      {c.sources.map((s) => (
                        <Badge key={s} variant="outline"
                               className={`text-[9px] ${s.includes('shared')
                                 ? 'text-emerald-700 border-emerald-200 bg-emerald-50' : ''}`}>
                          {s}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {c.memberships?.length > 0 && (
                    <p className="text-[10px] text-slate-400 mt-0.5 inline-flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {c.memberships.map((m) => `${m.church_name || 'a unit'}${m.role ? ` (${m.role})` : ''}`).join(', ')}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}

          {picked && (
            <div className="space-y-3 rounded-md border border-slate-200 p-3" data-testid="assign-options">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Role here</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="assign-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {roleGroups.map((g) => (
                        <SelectGroup key={g.key}>
                          <SelectLabel className="text-[10px] uppercase tracking-wide text-slate-400">{g.label}</SelectLabel>
                          {(g.roles || []).map((r) => (
                            <SelectItem key={r.key} value={r.key} data-testid={`assign-role-${r.key}`}>{r.label}</SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Start as</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="assign-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active straight away</SelectItem>
                      <SelectItem value="pending">Pending — for this unit to confirm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {picked.memberships?.length > 0 && (
                <div className="space-y-1.5">
                  <Label>They already belong somewhere</Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={mode === 'also' ? 'default' : 'outline'}
                            onClick={() => setMode('also')} data-testid="assign-mode-also">
                      Also serve here
                    </Button>
                    <Button type="button" size="sm" variant={mode === 'transfer' ? 'default' : 'outline'}
                            onClick={() => setMode('transfer')} data-testid="assign-mode-transfer">
                      <ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Transfer here
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-400">
                    {mode === 'also'
                      ? 'Their existing membership stays as it is.'
                      : 'Their other membership is closed and marked as transferred.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!picked || save.isPending} onClick={() => save.mutate()}
                  data-testid="assign-save">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Add to this roll
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignExistingMemberDialog;
